import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { db } from "../utils/offlineDB";
import { env } from "../constants/env";
import { calculateTotalScore } from "../utils/functions";
import { useNavigate, useParams } from "react-router-dom";

// Hook to handle certification forms (online + offline)
export const useCertification = (section, initialAnswers = {}) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { labId, questionnaireId } = useParams();

  const [formData, setFormData] = useState(initialAnswers);
  const [editMode, setEditMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [status, setStatus] = useState("Not Attached");

  const isReadOnly = !editMode;

  // --- Fetch questionnaire (offline/online) ---
  const fetchCertification = async () => {
    if (!questionnaireId) return; // New form
    setIsFetching(true);
    try {
      let data = null;

      if (!navigator.onLine) {
        // offline: load from Dexie
        data = await db.offlineCertifications.get(questionnaireId);
        console.log("Fetched offline data:", data);
        if (!data) throw new Error("No offline data found");
      } else {
        // online: fetch from server
        const res = await fetch(
          `${env.kataApiUrl}/api/certification/id/${questionnaireId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch certification");
        data = await res.json();

        console.log("Fetched online data:", data);

        // Save synced copy locally if no unsynced changes
        const existing = await db.offlineCertifications.get(data._id);

        console.log("Existing local data:", existing);

        if (!existing?.offline) {
          // Inside fetchCertification
          const offlineId =
            data._id && typeof data._id === "string"
              ? data._id
              : `offline-${crypto.randomUUID()}`;

          await db.offlineCertifications.put({
            ...data,
            id: offlineId, // ✅ ensure valid ID for Dexie
            offline: false,
            timestamp: Date.now(),
          });
        }
      }

      setStatus(data.status);
      const answers = {};
      (data.answers || []).forEach((ans) => {
        answers[ans.questionNumber] = {
          score: ans.score,
          comment: ans.comment,
          points: ans.points,
        };
      });

      setFormData(answers);
      setEditMode(data.status === "Not Attached" || !data.status);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    console.log(
      "useCertification mounted or labId/questionnaireId changed",
      labId,
      questionnaireId
    );
    if (labId) fetchCertification();
  }, [labId, questionnaireId]);

  // --- Handle answer changes ---
  const handleChange = (questionNumber, score, comment, points) => {
    if (!isReadOnly) {
      setFormData((prev) => ({
        ...prev,
        [questionNumber]: { score, comment, points },
      }));
    }
  };

  // --- Submit / Save ---
  const handleDone = async () => {
    try {
      if (Object.keys(formData).length === 0) {
        toast.warning("Please answer at least one question before submitting");
        return;
      }

      setLoading(true);

      const answersArray = Object.entries(formData).map(([q, data]) => ({
        questionNumber: q,
        score: data.score,
        comment: data.comment,
        points: data.points,
      }));

      const payload = {
        name: "SLIPTA",
        labId,
        section,
        auditorId: user.id,
        answers: answersArray,
        ...(questionnaireId && { _id: questionnaireId }),
      };

      const url = questionnaireId
        ? `${env.kataApiUrl}/api/certification/id/${questionnaireId}`
        : `${env.kataApiUrl}/api/certification/submit`;

      const method = questionnaireId ? "PUT" : "POST";

      // 🔌 OFFLINE
      if (!navigator.onLine) {
        // Save pending request
        await db.pendingRequests.add({
          url,
          method,
          body: payload,
          token,
          timestamp: Date.now(),
        });

        const offlineId = payload._id ?? `offline-${crypto.randomUUID()}`;

        await db.offlineCertifications.put({
          id: offlineId, // ✅ always valid key
          labId: payload.labId,
          section: payload.section,
          auditorId: payload.auditorId,
          name: payload.name,
          answers: payload.answers,
          status: "Not Attached",
          totalScore: calculateTotalScore(payload.answers),
          offline: true,
          timestamp: Date.now(),
        });

        toast.info(
          "You are offline. Assessment saved locally and will sync when online."
        );

        navigate(-1);
        return;
      }

      // 🌐 ONLINE
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Submission failed");
      }

      const data = await res.json();

      toast.success(
        questionnaireId ? "Updated successfully!" : "Submitted successfully!"
      );

      navigate(-1);
    } catch (err) {
      toast.error(err.message || "Failed to submit assessment");
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    handleChange,
    handleDone,
    editMode,
    isReadOnly,
    loading,
    isFetching,
    status,
    fetchCertification,
  };
};
